import React, { useRef, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Menu, MenuItem, Tab, Tabs } from '@mui/material';
import { ExpandMore as ExpandMoreIcon, Settings as SettingsIcon } from '@mui/icons-material';
import { useAccessControl } from '../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../hooks/usePermissions';
import ModulePageHeader from '../common/ModulePageHeader';
import ConfigSection from './ConfigSection';
import { configDescriptors } from './configSections';
import BankAccountsTab from './cashbank/BankAccountsTab';
import TransfersTab from './cashbank/TransfersTab';
import ChequesTab from './cashbank/ChequesTab';
import PettyCashTab from './cashbank/PettyCashTab';
import VouchersTab from './cashbank/VouchersTab';
import StatementsTab from './cashbank/StatementsTab';

// Day-to-day operational tabs - the main Tabs row.
const OPERATIONAL_TABS = [
  { value: 'accounts', label: 'Bank Accounts' },
  { value: 'transfers', label: 'Transfers' },
  { value: 'cheques', label: 'Cheques' },
  { value: 'petty', label: 'Petty Cash' },
  { value: 'vouchers', label: 'Vouchers' },
  { value: 'statements', label: 'Statements' },
];

// Setup screens that gate everything else on this page: an account can't
// appear under Bank Accounts, and a statement can't auto-match, until its
// reconciliation-account/payment-method-type/match-rule rows exist. Tucked
// behind a "Configuration" dropdown rather than crowding the main tab row -
// same pattern as CoaAccounts.js's ConfigMenu (Account Classes/Types/
// Sub-Types/Templates) so the two setup-heavy ledger screens read as one
// product.
const CONFIG_ITEMS = [
  { value: 'reconciliationAccounts', label: 'Reconciliation Accounts' },
  { value: 'paymentMethodTypes', label: 'Payment Method Types' },
  { value: 'matchRules', label: 'Match Rules' },
];

const ConfigMenu = ({ tab, setTab }) => {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const isConfigTab = CONFIG_ITEMS.some((i) => i.value === tab);
  const activeLabel = CONFIG_ITEMS.find((i) => i.value === tab)?.label;

  return (
    <>
      <Button
        ref={anchorRef}
        size="small"
        variant={isConfigTab ? 'contained' : 'outlined'}
        endIcon={<ExpandMoreIcon />}
        startIcon={<SettingsIcon />}
        onClick={() => setOpen((o) => !o)}
        onMouseEnter={() => setOpen(true)}
        sx={{ textTransform: 'none', fontWeight: isConfigTab ? 700 : 400, whiteSpace: 'nowrap' }}
      >
        {isConfigTab ? activeLabel : 'Setup'}
      </Button>
      <Menu
        anchorEl={anchorRef.current}
        open={open}
        onClose={() => setOpen(false)}
        MenuListProps={{ onMouseLeave: () => setOpen(false) }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ elevation: 2, sx: { minWidth: 210, mt: 0.5 } }}
      >
        {CONFIG_ITEMS.map((item) => (
          <MenuItem
            key={item.value}
            selected={tab === item.value}
            onClick={() => { setTab(item.value); setOpen(false); }}
            sx={{ fontSize: '0.875rem' }}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

// Cash & Bank: day-to-day cash operations over reconciliation-enabled GL
// accounts, plus the setup (reconciliation accounts, payment method types,
// match rules) that makes them appear. Completed bank reconciliations have
// their own menu item (Compliance > Bank Reconciliation).
const CashBank = () => {
  const { can } = useAccessControl();
  const [tab, setTab] = useState('accounts');

  if (!can(RESOURCES.LEDGER.CASH, ACTIONS.READ)) {
    return <Alert severity="error">You do not have permission to view Cash &amp; Bank.</Alert>;
  }

  const isConfigTab = CONFIG_ITEMS.some((i) => i.value === tab);

  return (
    <Box>
      <ModulePageHeader
        title="Cash & Bank"
        subtitle="Bank positions, transfers, cheques, petty cash, vouchers and statement matching"
      />
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ pb: '8px !important', pt: '4px !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Tabs
              value={isConfigTab ? false : tab}
              onChange={(_, v) => v && setTab(v)}
              variant="scrollable"
              scrollButtons="auto"
            >
              {OPERATIONAL_TABS.map((t) => (
                <Tab key={t.value} value={t.value} label={t.label} />
              ))}
            </Tabs>
            <ConfigMenu tab={tab} setTab={setTab} />
          </Box>
        </CardContent>
      </Card>
      {tab === 'accounts' && <BankAccountsTab />}
      {tab === 'transfers' && <TransfersTab />}
      {tab === 'cheques' && <ChequesTab />}
      {tab === 'petty' && <PettyCashTab />}
      {tab === 'vouchers' && <VouchersTab />}
      {tab === 'statements' && <StatementsTab />}
      {tab === 'reconciliationAccounts' && <ConfigSection descriptor={configDescriptors.reconciliationAccounts} />}
      {tab === 'paymentMethodTypes' && <ConfigSection descriptor={configDescriptors.paymentMethodTypes} />}
      {tab === 'matchRules' && <ConfigSection descriptor={configDescriptors.matchRules} />}
    </Box>
  );
};

export default CashBank;
